<template>
  <div class="kk__list">
    <div v-for="(item, index) in items" :key="index" class="kk__list--option">
      <radio-box :item="item" :is-checked="selectedOption === item.id" @passItem="selectOption" />
      <div v-if="item.subOptions" class="kk__list--subOptions kk__options--sub">
        <div
          v-for="(sub, subIndex) in item.subOptions"
          :key="subIndex"
          :class="{ active: activeSubOptionIndex === subIndex && item.id === selectedOption }"
        >
          <checkbox
            :item="sub"
            :parent="item"
            :is-checked="selectedSubOptions.some((opt) => opt.id === sub.id && opt.parentId === item.id)"
            @passItem="handleSubOption"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import hotkeys from "hotkeys-js";
import { HotkeyScopes, gotoType } from "../../../utils/constants";
import RadioBox from "../../commons/RadioBox.vue";
import Checkbox from "../../commons/Checkbox.vue";

export default {
  components: { RadioBox, Checkbox },
  data() {
    return {
      items: [
        {
          value: "comments",
          id: "03",
          label: this.$t("goTo.special.comments"),
        },
        {
          value: "constants",
          id: "04",
          label: this.$t("goTo.special.constants.label"),
          subOptions: [
            { id: 1, value: "numbers", label: this.$t("goTo.special.constants.options.numbers"), parentId: "04" },
            { id: 2, value: "text", label: this.$t("goTo.special.constants.options.text"), parentId: "04" },
          ],
        },
        {
          value: "formulas",
          id: "05",
          label: this.$t("goTo.special.formulas.label"),
          subOptions: [
            { id: 1, value: "numbers", label: this.$t("goTo.special.formulas.options.numbers"), parentId: "05" },
            { id: 2, value: "text", label: this.$t("goTo.special.formulas.options.text"), parentId: "05" },
            { id: 3, value: "logicals", label: this.$t("goTo.special.formulas.options.logicals"), parentId: "05" },
            { id: 4, value: "errors", label: this.$t("goTo.special.formulas.options.errors"), parentId: "05" },
          ],
        },
        { value: "blanks", id: "06", label: this.$t("goTo.special.blanks") },
        { value: "currentRegion", id: "07", label: this.$t("goTo.special.currentRegion") },
        { value: "currentArray", id: "08", label: this.$t("goTo.special.currentArray") },
        { value: "rowDifferences", id: "09", label: this.$t("goTo.special.rowDifferences") },
        { value: "columnDifferences", id: "10", label: this.$t("goTo.special.columnDifferences") },
        { value: "precedents", id: "11", label: this.$t("goTo.special.precedents") },
        {
          value: "dependents",
          id: "12",
          label: this.$t("goTo.special.dependents.label"),
          subOptions: [
            {
              id: 1,
              value: "directOnly",
              label: this.$t("goTo.special.dependents.options.directOnly"),
              parentId: "12",
            },
            { id: 2, value: "allLevel", label: this.$t("goTo.special.dependents.options.allLevel"), parentId: "12" },
          ],
        },

        { value: "visibleCells", id: "13", label: this.$t("goTo.special.visibleCells") },
        { value: "conditionalFormats", id: "14", label: this.$t("goTo.special.conditionalFormats") },
        { value: "dataValidation", id: "15", label: this.$t("goTo.special.dataValidation") },
      ],
      selectedOption: null,
      selectedOptionLabel: "", // Store the text of the selected option
      activeSubOptionIndex: null,
      currentSubOptions: [],
      selectedSubOptions: [],
      prevScope: null,
      keyPressed: null,
    };
  },
  mounted() {
    // Adding a small delay to ensure the scope is set correctly
    this.prevScope = hotkeys.getScope();
    this.$store.dispatch("setScope", HotkeyScopes.goto.shortcuts);

    hotkeys("escape", HotkeyScopes.goto.shortcuts, () => {
      this.$emit("hide");
    });

    hotkeys("up", HotkeyScopes.goto.shortcuts, (event) => {
      event.preventDefault();
      this.moveUp();
    });

    hotkeys("down", HotkeyScopes.goto.shortcuts, (event) => {
      event.preventDefault();
      this.moveDown();
    });

    hotkeys("space", HotkeyScopes.goto.shortcuts, (event) => {
      event.preventDefault();
      this.toggleActiveSubOption();
    });

    hotkeys("*", HotkeyScopes.goto.shortcuts, (event, handler) => {
      event.preventDefault();
      const isNumKey = event.code.startsWith("Numpad") || event.code.startsWith("Digit");
      if (!isNumKey) return;

      if (this.keyPressTimeout) clearTimeout(this.keyPressTimeout);

      const pressedKey = event.key;

      // If it's the first keypress
      if (this.keyPressed === null) {
        if (pressedKey === "0" || pressedKey === "1") {
          this.keyPressed = pressedKey; // Wait for second digit
        } else {
          // If first digit is invalid (2-9), ignore
          this.keyPressed = null;
          return;
        }
      } else {
        // If second digit is entered, complete the selection
        this.keyPressed += pressedKey;
        const selectedNumber = parseInt(this.keyPressed, 10);

        // Special case for "01", emit navigate
        if (this.keyPressed === "01") {
          this.$emit("navigate", gotoType.cell);
        } else if (selectedNumber >= 1 && selectedNumber <= 15) {
          this.handleSelection(this.keyPressed.padStart(2, "0"));
        }

        // Reset after processing
        this.keyPressed = null;
        return;
      }

      // If waiting for a second digit, reset after a short timeout
      this.keyPressTimeout = setTimeout(() => {
        this.keyPressed = null;
      }, 300);
    });
  },

  beforeUnmount() {
    this.$store.dispatch("setScope", this.prevScope);
    hotkeys.unbind("*,up,down,space", HotkeyScopes.goto.shortcuts);
    this.$emit("passOption", null);
  },
  methods: {
    handleSelection(selectedId) {
      this.selectedOption = selectedId;
      // Find the label of the selected option
      const selectedItem = this.items.find((item) => item.id === selectedId);

      // Guard condition: If no matching item is found, return early
      if (!selectedItem) {
        return;
      }

      if (selectedItem) {
        this.selectedOptionLabel = selectedItem.label; // Store the label
        this.passOptionLabel();
      }
      if (selectedItem?.subOptions) {
        this.selectOption(selectedItem);
      }
      if (!selectedItem.subOptions) {
        this.currentSubOptions = [];
        this.activeSubOptionIndex = null;
      }
      // Reset sub-options when switching between options
      this.clearSelectedSubOptions();
    },
    selectOption(option) {
      this.selectedOption = option.id;
      this.setLabelAndSubOptions(option);
      // Reset sub-options when switching between options
      this.clearSelectedSubOptions();
      this.passOptionLabel();
    },
    passOptionLabel() {
      this.$emit("passOption", this.selectedOptionLabel);
    },

    handleSubOption(item) {
      // Ensure the parent is selected
      if (this.selectedOption !== item?.parentId) {
        this.selectedOption = item?.parentId;
        this.clearSelectedSubOptions();
      }
      const parent = this.items.find((itm) => itm.id == item?.parentId);
      this.setLabelAndSubOptions(parent, item);
      this.toggleActiveSubOption();
      this.passOptionLabel();
    },
    setLabelAndSubOptions(option, sub = null) {
      this.selectedOptionLabel = option.label;
      this.currentSubOptions = option.subOptions || [];
      if (sub) {
        this.activeSubOptionIndex = this.currentSubOptions.indexOf(sub);
      } else this.activeSubOptionIndex = this.currentSubOptions.length > 0 ? 0 : null;
    },
    moveDown() {
      if (!this.currentSubOptions.length) return;
      this.activeSubOptionIndex = (this.activeSubOptionIndex + 1) % this.currentSubOptions.length;
    },

    moveUp() {
      if (!this.currentSubOptions.length) return;
      this.activeSubOptionIndex =
        this.activeSubOptionIndex <= 0
          ? this.currentSubOptions.length - 1
          : (this.activeSubOptionIndex - 1) % this.currentSubOptions.length;
    },

    toggleActiveSubOption() {
      if (this.activeSubOptionIndex === null || !this.currentSubOptions.length) return;

      const activeSubOption = this.currentSubOptions[this.activeSubOptionIndex];
      const index = this.selectedSubOptions.indexOf(activeSubOption);

      if (index === -1) {
        this.selectedSubOptions.push(activeSubOption);
      } else {
        this.selectedSubOptions.splice(index, 1);
      }
    },
    clearSelectedSubOptions() {
      this.selectedSubOptions = [];
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../../assets/styles/variables.less";

.kk__list {
  column-count: 2;
  gap: 4rem;
  color: @kk-color-primary-blue;
  font-weight: 500;

  label {
    cursor: pointer;
    width: max-content;
  }

  &--option {
    margin-bottom: 0.5rem;
    label {
      display: flex;
      gap: 1.3rem;
      align-items: center;
    }
  }

  &--subOptions {
    margin-left: 2.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    color: rgba(8, 50, 119, 0.5);
    padding-top: 0.6rem;
    div.active {
      color: @kk-color-primary-blue;
    }
  }
}
</style>
